import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Snackbar from '@mui/material/Snackbar';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import SearchIcon from '@mui/icons-material/Search';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Tooltip from '@mui/material/Tooltip';
import UserListV2 from './UserListV2';
import AddToCampaignModal from '../InfluencerList/AddToCampaignModal';
import AudienceUniqueReachModal from './AudienceUniqueReachModal';
import CreatorTrustModal from './CreatorTrustModal';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ListIcon from '../../../../assets/discovery/list-icon.svg';
import { getPlannerDownload } from 'api_handlers/discovery';
import { showPremiumFeatureDialog } from 'actions/planner2';

// --- Claude-style "thinking steps" shown while the AI search runs ---
const THINK_STEPS = [
  { label: 'Understanding your request', Icon: PsychologyOutlinedIcon },
  { label: 'Scanning the creator index', Icon: SearchIcon },
  { label: 'Analyzing engagement & audience fit', Icon: InsightsOutlinedIcon },
  { label: 'Ranking the best matches', Icon: AutoAwesomeIcon },
];

const AiThinkingSteps = () => {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(
      () => setActive((a) => Math.min(a + 1, THINK_STEPS.length - 1)),
      1100,
    );
    return () => clearInterval(t);
  }, []);
  return (
    <Box sx={{ py: 5, px: 3, maxWidth: 440, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5, justifyContent: 'center' }}>
        <AutoAwesomeIcon sx={{ color: '#febd1c' }} />
        <Typography sx={{ fontWeight: 800, color: '#1f2433' }}>AI is thinking…</Typography>
      </Box>
      <Stack spacing={1.5}>
        {THINK_STEPS.map((s, i) => {
          const done = i < active;
          const current = i === active;
          const StepIcon = s.Icon;
          return (
            <Box
              key={s.label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                opacity: i > active ? 0.45 : 1,
                transition: 'opacity .3s ease',
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '9px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  background: done ? '#e9f8ee' : current ? '#fff7e2' : '#eef1f8',
                }}
              >
                {done ? (
                  <CheckCircleIcon sx={{ fontSize: 18, color: '#15803d' }} />
                ) : current ? (
                  <CircularProgress size={15} sx={{ color: '#c79309' }} />
                ) : (
                  <StepIcon sx={{ fontSize: 16, color: '#9aa1b2' }} />
                )}
              </Box>
              <Typography
                sx={{
                  fontSize: '13.5px',
                  fontWeight: current ? 700 : 600,
                  color: done ? '#15803d' : current ? '#1f2433' : '#9aa1b2',
                }}
              >
                {s.label}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'auto' });

// AI-mode results — reuses the exact manual-mode table (UserListV2) so the
// header / icons / columns / colours are identical, with an AI-styled header,
// a professional empty state, and AI (prop-driven) pagination.
const AiInfluencerListV2 = ({
  influencers,
  totalResults,
  loading,
  onSortChange,
  onPageChange,
  useCategorySort,
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.planner2.user);
  const selectedInfluencers = useSelector((state) => state.planner2.selectedInfluencers);

  const [sortBy, setSortBy] = useState('relevancy');
  const [page, setPage] = useState(1);
  const [openAddToCampaign, setOpenAddToCampaign] = useState(false);
  const [openModalCondition, setOpenModalCondition] = useState('');
  const [addToCampaignInfluencers, setAddToCampaignInfluencers] = useState([]);
  const [openReach, setOpenReach] = useState(false);
  const [openTrust, setOpenTrust] = useState(false);

  const hasResults = influencers && influencers.length > 0;
  const showEmpty = !loading && !hasResults;

  const handleSortByChange = (event) => {
    setSortBy(event.target.value);
    onSortChange && onSortChange(event.target.value);
  };

  const handleAddToCampaign = (influencerIds, isAuth) => {
    setOpenModalCondition(isAuth);
    const infs = influencers.filter((inf) => influencerIds.includes(inf.id));
    setAddToCampaignInfluencers(infs);
    setOpenAddToCampaign(true);
  };

  const handleDownload = () => {
    getPlannerDownload({
      selected_ids: Array.from(selectedInfluencers),
      objective: '',
      prediction: {},
      contacts: 'not_req',
      selectedColumns: [],
    });
  };

  return (
    <Box className="dv2-surface" sx={{ overflow: 'hidden' }}>
      {/* header */}
      <Box
        sx={{
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #eef0f5',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '10px',
              background: 'linear-gradient(135deg,#111936,#26315f)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AutoAwesomeIcon sx={{ color: '#febd1c', fontSize: 19 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.02rem', color: '#1f2433', lineHeight: 1.1 }}>
              AI Recommended Influencers
              {hasResults ? ` (${parseInt(totalResults || 0).toLocaleString('en-IN')})` : ''}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#6b7280' }}>
              {showEmpty ? 'Describe who you need above' : 'Ranked by AI relevance'}
            </Typography>
          </Box>
        </Box>

        <FormControl size="small" sx={{ minWidth: 150, '& .MuiInputLabel-root': { top: '2px' } }}>
          <InputLabel id="dv2-ai-sort">Sort by</InputLabel>
          <Select labelId="dv2-ai-sort" value={sortBy} onChange={handleSortByChange} label="Sort by">
            {useCategorySort && <MenuItem value={'relevancy'}>Relevancy</MenuItem>}
            <MenuItem value={'followers'}>Followers</MenuItem>
            <MenuItem value={'views'}>Avg Views</MenuItem>
            <MenuItem value={'eng-rate'}>Engagement Rate</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* body */}
      {loading ? (
        <AiThinkingSteps />
      ) : showEmpty ? (
        <Box
          sx={{
            m: { xs: 2, md: 3 },
            py: 6,
            px: 3,
            textAlign: 'center',
            borderRadius: '16px',
            background: 'linear-gradient(135deg,#f4f1ff 0%,#eef5ff 50%,#fff7e6 100%)',
            border: '1px dashed #d9def0',
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              mx: 'auto',
              mb: 2,
              borderRadius: '18px',
              background: 'linear-gradient(135deg,#111936,#2b3a78)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 12px 26px -10px rgba(17,25,54,0.5)',
            }}
          >
            <AutoAwesomeIcon sx={{ color: '#febd1c', fontSize: 30 }} />
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: '#1f2433' }}>
            Let AI find your perfect creators
          </Typography>
          <Typography sx={{ color: '#5b6478', mt: 1, maxWidth: 460, mx: 'auto', fontSize: '13.5px' }}>
            Describe who you’re looking for in plain language — niche, location, audience or vibe —
            and we’ll surface the best-matched influencers, ranked by relevance.
          </Typography>
          <Box
            sx={{
              mt: 2.5,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: '999px',
              background: 'rgba(254,189,28,0.16)',
              border: '1px solid rgba(254,189,28,0.4)',
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 16, color: '#c79309' }} />
            <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: '#7a5e10' }}>
              Try: “fitness creators from South India with high engagement”
            </Typography>
          </Box>
        </Box>
      ) : (
        <UserListV2 influencers={influencers} loading={loading} handleAddToCampaign={handleAddToCampaign} aiMode />
      )}

      {/* pagination */}
      {hasResults ? (
        <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center', borderTop: '1px solid #eef0f5' }}>
          <Pagination
            count={Math.max(1, Math.ceil((totalResults || 0) / 20))}
            color="primary"
            page={page}
            onChange={(e, updatedPage) => {
              if (user.premium_user || updatedPage < 4) {
                setPage(updatedPage);
                onPageChange && onPageChange(updatedPage);
                scrollToTop();
              } else {
                dispatch(showPremiumFeatureDialog(true));
              }
            }}
          />
        </Box>
      ) : null}

      {/* selection action bar (same as manual) */}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={selectedInfluencers.size > 0 && !openAddToCampaign}
      >
        <Box
          sx={{
            p: 2,
            bgcolor: '#111936',
            borderRadius: 2,
            boxShadow: '0 16px 40px -12px rgba(17,25,54,0.6)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'nowrap',
          }}
        >
          <Typography variant="h4" sx={{ color: '#ffffff', fontWeight: 700, whiteSpace: 'nowrap', mr: 1 }}>
            Selected {selectedInfluencers.size} influencers
          </Typography>
          <Button
            variant="contained"
            color="primary"
            sx={{ whiteSpace: 'nowrap' }}
            onClick={() => handleAddToCampaign(Array.from(selectedInfluencers), 'Planner')}
            startIcon={<img src={ListIcon} style={{ width: '12px' }} alt="" />}
          >
            Add to List
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ whiteSpace: 'nowrap' }}
            onClick={() => {
              if (user.premium_user) handleAddToCampaign(Array.from(selectedInfluencers), 'AddToCampaign');
              else dispatch(showPremiumFeatureDialog(true));
            }}
            startIcon={<WorkspacePremiumIcon />}
          >
            Add to Campaign
          </Button>
          <Button
            variant="contained"
            color="primary"
            sx={{ whiteSpace: 'nowrap' }}
            onClick={() => {
              if (user.premium_user) handleDownload();
              else dispatch(showPremiumFeatureDialog(true));
            }}
            startIcon={<WorkspacePremiumIcon />}
          >
            Download
          </Button>
          {selectedInfluencers.size >= 2 && selectedInfluencers.size <= 10 && (
            <Tooltip title="Estimate unique vs redundant audience across the selected creators">
              <Button
                variant="contained"
                color="primary"
                sx={{ whiteSpace: 'nowrap' }}
                onClick={() => setOpenReach(true)}
                startIcon={<InsightsOutlinedIcon />}
              >
                Unique Reach
              </Button>
            </Tooltip>
          )}
          {selectedInfluencers.size === 1 && (
            <Tooltip title="Run the Creator Trust vetting (gates + evidence) on this creator">
              <Button
                variant="contained"
                color="primary"
                sx={{ whiteSpace: 'nowrap' }}
                onClick={() => setOpenTrust(true)}
                startIcon={<VerifiedUserIcon />}
              >
                Trust Check
              </Button>
            </Tooltip>
          )}
        </Box>
      </Snackbar>

      <AudienceUniqueReachModal
        open={openReach}
        setOpen={setOpenReach}
        ids={Array.from(selectedInfluencers)}
      />

      <CreatorTrustModal
        open={openTrust}
        setOpen={setOpenTrust}
        id={Array.from(selectedInfluencers)[0]}
      />

      <AddToCampaignModal
        v2
        open={openAddToCampaign}
        setOpen={setOpenAddToCampaign}
        influencers={addToCampaignInfluencers}
        text={openModalCondition}
      />
    </Box>
  );
};

export default AiInfluencerListV2;
